import { useState, type ReactNode } from "react";
import { Alert, Colors, Icon } from "swing-components";

import {
  AlertError,
  ButtonScore,
  ModalCancelCommitmentsCancellationReason,
  ModalCancelCommitmentsCancellationType,
  ModalCancelCommitmentsPartialCancelDays,
} from "~components";
import {
  translate,
  type CancelPenaltyType,
  type PartialCancelRequestPayload,
  type RequestData,
} from "~utils";

import "./ModalCancelCommitments.css"; // Note .css as ionic overrides needed

import { DateTime } from "luxon";

import { PotentialPenaltyAlert } from "./PotentialPenaltyAlert";

/********* Constants ********/
export const CancelReason: Record<CancelReasonType, string> = {
  CANCEL_REASON_SCHEDULE_CONFLICT: translate("CANCEL_REASON_SCHEDULE_CONFLICT"),
  CANCEL_REASON_SICKNESS: translate("CANCEL_REASON_SICKNESS"),
  CANCEL_REASON_CLICKED_BY_ACCIDENT: translate("CANCEL_REASON_CLICKED_BY_ACCIDENT"),
  CANCEL_REASON_OTHER: translate("LABEL_OTHER"),
};

/********* Types ********/
export type ModalCancelCommitmentsStep =
  | "cancellation-type" // Cancel some days or all remaining days option
  | "partial-cancel-days" // Cancel some days input
  | "cancellation-reason"; // Cancellation reason radio group

export type CancellationType = "CANCEL_PARTIAL_REQUEST" | "CANCEL_FULL_REQUEST";

// Includes CancelPenaltyType values from calcCancelPenaltyType (threshold-based),
// plus "POTENTIAL_PENALTY", which depends on the user's selected cancellation type.
// This value drives which alert is shown in <PenaltyAlert/>.
export type PenaltyAlert = CancelPenaltyType | "POTENTIAL_PENALTY";

export type CancelReasonType =
  | "CANCEL_REASON_SCHEDULE_CONFLICT"
  | "CANCEL_REASON_SICKNESS"
  | "CANCEL_REASON_CLICKED_BY_ACCIDENT"
  | "CANCEL_REASON_OTHER";

export type ModalCancelCommitmentsProps = {
  hasApiError: boolean;
  onDismiss: () => void;
  onConfirm: (cancelPayload: CancelFormState | PartialCancelRequestPayload) => void;
  penaltyType: CancelPenaltyType;
  step?: ModalCancelCommitmentsStep;
  intervals: RequestData["intervals"];
  // Whether the onConfirm action is pending
  isOnConfirmPending?: boolean;
  // Optional DateTime to use for testing purposes when calculating remaining days
  // TODO: Remove this once we have a proper way to mock the time in storybook
  overrideCurrentTime?: DateTime;
};

export type CancelFormState = {
  reason?: CancelReasonType;
  explanation?: string | null | undefined;
  partialCancelText?: string;
};

export function ModalCancelCommitments(props: ModalCancelCommitmentsProps) {
  const {
    hasApiError,
    onDismiss,
    onConfirm,
    penaltyType,
    step,
    intervals,
    overrideCurrentTime,
    isOnConfirmPending,
  } = props;

  /********* State ********/
  const [cancelFormData, setCancelFormData] = useState<CancelFormState>({});
  const [_step, _setStep] = useState<ModalCancelCommitmentsStep>(step ?? "cancellation-reason");
  const [cancellationType, setCancellationType] = useState<CancellationType | undefined>();

  /********* Render ********/
  switch (_step) {
    case "cancellation-type":
      return (
        <ModalCancelCommitmentsCancellationType
          penaltyType="POTENTIAL_PENALTY"
          setStep={_setStep}
          onDismiss={onDismiss}
          cancellationType={cancellationType}
          setCancellationType={setCancellationType}
        />
      );
    case "partial-cancel-days":
      return (
        <ModalCancelCommitmentsPartialCancelDays
          penaltyType="POTENTIAL_PENALTY"
          setStep={_setStep}
          onDismiss={onDismiss}
          cancelFormData={cancelFormData}
          setCancelFormData={setCancelFormData}
        />
      );
    case "cancellation-reason":
      return (
        <ModalCancelCommitmentsCancellationReason
          onDismiss={onDismiss}
          hasApiError={hasApiError}
          cancelFormData={cancelFormData}
          onConfirm={onConfirm}
          setCancelFormData={setCancelFormData}
          penaltyType={
            // If the user is cancelling some days,
            // we show the potential penalty alert
            cancellationType === "CANCEL_PARTIAL_REQUEST" ? "POTENTIAL_PENALTY" : penaltyType
          }
          intervals={intervals}
          overrideCurrentTime={overrideCurrentTime}
          isOnConfirmPending={isOnConfirmPending}
        />
      );
    default:
      throw new Error(translate("CANCEL_COMMITMENT_MODAL_STEP_ERROR_MESSAGE", _step));
  }
}

/********* Helper Components ********/
type ModalCancelCommitmentsHeaderProps = {
  onDismiss: () => void;
};

export function ModalCancelCommitmentsHeader(props: ModalCancelCommitmentsHeaderProps) {
  const { onDismiss } = props;

  return (
    <div className="modal-header">
      <h2>{translate("CANCEL_COMMITMENT_MODAL_TITLE")}</h2>
      <button onClick={onDismiss} css={{ backgroundColor: "transparent" }}>
        <Icon name="Close" color={Colors.blue500} />
      </button>
    </div>
  );
}

type ModalCancelCommitmentsBodyProps = {
  children: ReactNode;
  penaltyType: PenaltyAlert;
  hasApiError?: boolean;
};

export function ModalCancelCommitmentsBody(props: ModalCancelCommitmentsBodyProps) {
  const { children, hasApiError, penaltyType } = props;

  return (
    <div className="modal-body">
      {hasApiError && <AlertError />}
      <PenaltyAlert penaltyType={penaltyType} />
      {children}
    </div>
  );
}

type ModalCancelCommitmentsFooterProps = {
  buttonPrimary: {
    onClick?: () => void;
    isDisabled: boolean;
    text: string;
    isActionPending?: boolean;
  };
  buttonSecondary: {
    onClick?: () => void;
    isDisabled: boolean;
    text: string;
  };
};

export function ModalCancelCommitmentsFooter(props: ModalCancelCommitmentsFooterProps) {
  const { buttonPrimary, buttonSecondary } = props;

  return (
    <div className="modal-footer two-buttons">
      <div className="action-button">
        <ButtonScore
          disabled={buttonPrimary.isDisabled}
          label={buttonPrimary.text}
          expand="block"
          onClick={!buttonPrimary.isDisabled ? buttonPrimary.onClick : undefined}
          isActionPending={buttonPrimary.isActionPending}
        />
      </div>
      <div className="action-button">
        <ButtonScore
          fill="outline"
          disabled={buttonSecondary.isDisabled}
          label={buttonSecondary.text}
          onClick={!buttonSecondary.isDisabled ? buttonSecondary.onClick : undefined}
        />
      </div>
    </div>
  );
}

type PenaltyAlertProps = {
  penaltyType: PenaltyAlert;
};

function PenaltyAlert(props: PenaltyAlertProps) {
  const { penaltyType } = props;

  switch (penaltyType) {
    case "PENALTY_REQUEST_NOT_STARTED":
      return (
        <div css={{ marginBottom: 16 }}>
          <Alert color="warning" showIcon title={translate("CANCEL_LAST_MINUTE_TITLE")}>
            {translate("CANCEL_LAST_MINUTE_WITHIN_DAY")}.{" "}
            {translate("CANCEL_LAST_MINUTE_IMPACT_MESSAGE")}.
          </Alert>
        </div>
      );
    case "PENALTY_REQUEST_STARTED":
      return (
        <div css={{ marginBottom: 16 }}>
          <Alert color="warning" showIcon title={translate("CANCEL_LAST_MINUTE_TITLE")}>
            {translate("CANCEL_LAST_MINUTE_ALREADY_STARTED")}.{" "}
            {translate("CANCEL_LAST_MINUTE_IMPACT_MESSAGE")}.
          </Alert>
        </div>
      );
    case "POTENTIAL_PENALTY":
      return (
        <div css={{ marginBottom: 16 }}>
          <PotentialPenaltyAlert />
        </div>
      );
    default:
      return <></>;
  }
}
